/**
 * @file Generateur pseudo-aleatoire a graine (sfc32 initialise par splitmix32) : deterministe, sauvegardable, forkable.
 */

const splitmix32 = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x9e3779b9) >>> 0;
    let t = a ^ (a >>> 16);
    t = Math.imul(t, 0x21f0aaad);
    t ^= t >>> 15;
    t = Math.imul(t, 0x735a2d97);
    t ^= t >>> 15;
    return t >>> 0;
  };
};

export interface RngState {
  a: number;
  b: number;
  c: number;
  d: number;
}

/** FNV-1a 32 bits d'une chaine : sert a deriver des graines de flux nommes. */
export const hashString = (s: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

export class Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  constructor(seed: number) {
    const sm = splitmix32(seed);
    this.a = sm();
    this.b = sm();
    this.c = sm();
    this.d = sm();
    for (let i = 0; i < 12; i++) this.nextU32(); // echauffement
  }

  nextU32(): number {
    const t = (((this.a + this.b) >>> 0) + this.d) >>> 0;
    this.d = (this.d + 1) >>> 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) >>> 0;
    this.c = ((this.c << 21) | (this.c >>> 11)) >>> 0;
    this.c = (this.c + t) >>> 0;
    return t;
  }

  /** Flottant dans [0, 1). */
  next(): number {
    return this.nextU32() / 4294967296;
  }

  /** Flottant dans [min, max). */
  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /** Entier dans [min, max] (bornes incluses). */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  sign(): 1 | -1 {
    return this.next() < 0.5 ? -1 : 1;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick: empty array');
    return items[Math.floor(this.next() * items.length)] as T;
  }

  /** Tirage pondere ; poids <= 0 ignores. Retourne null si tous les poids sont nuls. */
  weighted<T>(items: readonly T[], weightOf: (item: T) => number): T | null {
    let total = 0;
    for (const it of items) total += Math.max(0, weightOf(it));
    if (total <= 0) return null;
    let r = this.next() * total;
    for (const it of items) {
      const w = Math.max(0, weightOf(it));
      if (r < w) return it;
      r -= w;
    }
    return items[items.length - 1] ?? null;
  }

  /** Melange de Fisher-Yates en place. */
  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      const tmp = arr[i] as T;
      arr[i] = arr[j] as T;
      arr[j] = tmp;
    }
    return arr;
  }

  /** Approximation gaussienne (somme de 3 uniformes) centree sur 0, dans [-1, 1]. */
  gauss(): number {
    return (this.next() + this.next() + this.next() - 1.5) / 1.5;
  }

  /** Cree un flux independant derive de l'etat courant et d'un label (n'avance pas ce flux). */
  fork(label: string): Rng {
    return new Rng((this.a ^ hashString(label) ^ Math.imul(this.d, 2654435761)) >>> 0);
  }

  getState(): RngState {
    return { a: this.a, b: this.b, c: this.c, d: this.d };
  }

  setState(s: RngState): void {
    this.a = s.a >>> 0;
    this.b = s.b >>> 0;
    this.c = s.c >>> 0;
    this.d = s.d >>> 0;
  }
}

/** Flux nommes independants : modifier la consommation d'un flux ne perturbe pas les autres. */
export class RngStreams {
  readonly combat: Rng;
  readonly spawn: Rng;
  readonly loot: Rng;
  readonly gacha: Rng;
  readonly misc: Rng;

  constructor(seed: number) {
    const master = new Rng(seed);
    this.combat = master.fork('combat');
    this.spawn = master.fork('spawn');
    this.loot = master.fork('loot');
    this.gacha = master.fork('gacha');
    this.misc = master.fork('misc');
  }

  getState(): Record<'combat' | 'spawn' | 'loot' | 'gacha' | 'misc', RngState> {
    return {
      combat: this.combat.getState(),
      spawn: this.spawn.getState(),
      loot: this.loot.getState(),
      gacha: this.gacha.getState(),
      misc: this.misc.getState(),
    };
  }

  setState(s: Record<'combat' | 'spawn' | 'loot' | 'gacha' | 'misc', RngState>): void {
    this.combat.setState(s.combat);
    this.spawn.setState(s.spawn);
    this.loot.setState(s.loot);
    this.gacha.setState(s.gacha);
    this.misc.setState(s.misc);
  }
}
